import Anthropic from '@anthropic-ai/sdk';
import {fetchAvailableModels,recommendModel} from './anthropicModels.js';
import {ANTHROPIC_FUNCTION_PATH,canUseNetlifyFunction,isAnthropicFunctionAvailable} from './netlifyFunction.js';

let client=null;
let currentKey=null;
let currentBase='direct';
const resolvedModels=new Map();

const FALLBACK_MODEL_IDS=[
  'claude-sonnet-4-5',
  'claude-haiku-4-5',
  'claude-sonnet-4-0',
  'claude-3-7-sonnet-20250219',
  'claude-3-5-sonnet-20241022',
];

const validateApiKey=(apiKey)=> {
  const key=apiKey?.trim();
  if (!key?.startsWith('sk-ant-') || key.length < 40) {
    throw new Error('Enter your complete Anthropic API key.');
  }
  return key;
};

const normaliseProxy=(proxyUrl)=> {
  const trimmed=proxyUrl?.trim();
  if (!trimmed) return null;
  try {
    const url=new URL(trimmed);
    if (!['https:','http:'].includes(url.protocol)) return null;
    return url.toString().replace(/\/+$/,'');
  } catch {
    return null;
  }
};

/**
 * Return the ABSOLUTE base URL for the Netlify serverless function.
 * The Anthropic SDK requires an absolute URL — a relative path breaks
 * `new URL()` resolution inside the SDK and causes the "cannot reach"
 * error on Netlify deployments.
 */
const getNetlifyFunctionBase=()=> {
  if (!canUseNetlifyFunction()) return null;
  try {
    return new URL(ANTHROPIC_FUNCTION_PATH, window.location.origin)
      .toString()
      .replace(/\/+$/,'');
  } catch {
    return null;
  }
};

export const getAnthropicClient=(apiKey,proxyUrl=null,useFunction=true)=> {
  const key=validateApiKey(apiKey);
  const proxy=normaliseProxy(proxyUrl);
  const baseURL=proxy || (useFunction ? getNetlifyFunctionBase() : null);
  const cacheKey=`${key}:${baseURL || 'direct'}`;

  if (!client || currentKey!==key || currentBase!==(baseURL || 'direct')) {
    const options={
      apiKey: key,
      dangerouslyAllowBrowser: true,
      // Large generations (up to 8000 output tokens) can take several
      // minutes; 60s cut them off. 10 minutes matches the SDK default.
      timeout: 600000,
      maxRetries: 0,
    };
    if (baseURL) options.baseURL=baseURL;
    client=new Anthropic(options);
    currentKey=key;
    currentBase=baseURL || 'direct';
    resolvedModels.clear();
  }
  return {client,cacheKey};
};

const statusOf=(error)=> error?.status ?? error?.statusCode ?? error?.response?.status;
const isUnavailable=(error)=> [403,404].includes(statusOf(error));
const isTransient=(error)=> [429,500,502,503,529].includes(statusOf(error));

/**
 * Messages calls go DIRECT to Anthropic first. Netlify kills functions after
 * 30 seconds, and long generations take longer than that, which surfaced as
 * `502 {"errorType":"Error","errorMessage":"An unknown error has occurred"}`.
 * The Netlify function is only a fallback for when the browser cannot reach
 * Anthropic at all (network/CORS failure, not a timeout).
 *
 * Returns the route to switch to (true = function, false = direct), or null
 * to keep the current route.
 */
const switchRouteFor=async (error,useFunction,proxyUrl)=> {
  if (proxyUrl) return null;
  if (useFunction) {
    // Function missing (404) or killed by Netlify's time limit (502/504).
    return [404,502,504].includes(statusOf(error)) ? false : null;
  }
  const cannotConnect=error instanceof Anthropic.APIConnectionError &&
    !(error instanceof Anthropic.APIConnectionTimeoutError);
  return cannotConnect && await isAnthropicFunctionAvailable() ? true : null;
};

const createMessage=async (key,proxyUrl,request)=> {
  let useFunction=false;
  let {client}=getAnthropicClient(key,proxyUrl,useFunction);
  try {
    return await client.messages.create(request);
  } catch (error) {
    const next=await switchRouteFor(error,useFunction,proxyUrl);
    if (next===null) throw error;
    useFunction=next;
    ({client}=getAnthropicClient(key,proxyUrl,useFunction));
    return client.messages.create(request);
  }
};

export const buildHumanError=(error)=> {
  const status=statusOf(error);
  if (status===401) return 'Anthropic rejected this API key (401).';
  if (status===403 || status===404) {
    return `Model unavailable (${status}). Select another model or check access for this API key.`;
  }
  if (status===429) return 'Anthropic rate limit reached (429). Try again shortly.';
  return error?.message || String(error);
};

export const probeModel=async (apiKey,modelId,proxyUrl=null)=> {
  try {
    await createMessage(validateApiKey(apiKey),proxyUrl,{
      model: modelId,
      max_tokens: 5,
      messages: [{role: 'user',content: 'Hi'}],
    });
    return {ok: true,error: null};
  } catch (error) {
    return {ok: false,error: buildHumanError(error)};
  }
};

const getCandidates=async (apiKey,selectedModel,proxyUrl)=> {
  let listedIds=[];
  let recommended='';
  try {
    const models=await fetchAvailableModels(apiKey,null,proxyUrl);
    listedIds=models.map((model)=> model.id);
    recommended=recommendModel(models);
  } catch {
    // Model discovery may be blocked even when Messages API requests work.
    // Continue with the selected model rather than aborting generation.
  }
  return [...new Set([
    selectedModel,
    recommended,
    ...listedIds,
    ...FALLBACK_MODEL_IDS,
  ].filter(Boolean))];
};

const extractText=(message)=> (
  message.content
    ?.filter((block)=> block.type==='text')
    .map((block)=> block.text)
    .join('') || ''
);

/**
 * Same as callAnthropicMessageWithMeta() but returns only the text.
 */
export const callAnthropicMessage=async (...args)=> (
  (await callAnthropicMessageWithMeta(...args)).text
);

/**
 * Send one Messages request and return {text, stopReason, model}.
 * stopReason is 'max_tokens' when the answer was cut off at the token limit.
 */
export const callAnthropicMessageWithMeta=async (
  apiKey,
  model,
  systemPrompt,
  userPrompt,
  maxTokens=4096,
  maxRetries=4,
  onRetry=null,
  proxyUrl=null
)=> {
  const key=validateApiKey(apiKey);
  const cacheKey=`${key}:${normaliseProxy(proxyUrl) || 'direct'}`;
  const cached=resolvedModels.get(cacheKey);
  const candidates=cached ? [cached] : await getCandidates(key,model,proxyUrl);
  let lastUnavailable=null;

  for (const candidate of candidates) {
    for (let attempt=0;attempt <=maxRetries;attempt++) {
      try {
        // createMessage picks the route (direct first, function fallback).
        const message=await createMessage(key,proxyUrl,{
          model: candidate,
          max_tokens: maxTokens,
          system: systemPrompt,
          messages: [{role: 'user',content: userPrompt}],
        });
        const text=extractText(message);
        if (!text) throw new Error('Anthropic returned an empty response.');
        resolvedModels.set(cacheKey,candidate);
        return {text,stopReason: message.stop_reason,model: candidate};
      } catch (error) {
        if (isUnavailable(error)) {
          lastUnavailable=error;
          resolvedModels.delete(cacheKey);
          break;
        }
        if (!isTransient(error) || attempt===maxRetries) {
          throw new Error(buildHumanError(error));
        }
        const delay=Math.min(2000 * 2 ** attempt,30000);
        onRetry?.(attempt + 1,delay,error);
        await new Promise((resolve)=> setTimeout(resolve,delay));
      }
    }
  }

  throw new Error(
    lastUnavailable
      ? `None of the selected or fallback Anthropic models is accessible with this API key. Last error: ${lastUnavailable.message || buildHumanError(lastUnavailable)}`
      : 'No Anthropic model could be selected.'
  );
};